import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // Verificar que existe tipo_usuario_id
        if (!parsedUser.tipo_usuario_id) {
          // Si no existe, limpiar storage y forzar nuevo login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        } else {
          setToken(storedToken);
          setUser(parsedUser);
        }
      } else {
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    try {
      // Verificar que userData tenga tipo_usuario_id
      if (!userData.tipo_usuario_id) {
        throw new Error('Datos de usuario incompletos');
      }
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);
    } catch (error) {
      console.error('Error during login:', error);
      throw new Error('Error al iniciar sesión');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tempUserId');
    setToken(null);
    setUser(null);
  };

  // Función para manejar logout automático cuando el token expire
  const handleTokenExpired = () => {
    console.log('Token expirado, cerrando sesión automáticamente...');
    logout();
    // Opcional: mostrar un mensaje al usuario
    alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
  };

  const refreshToken = (newToken) => {
    try {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
    }
  };

  // Función para obtener el token actual
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Interceptor para manejar respuestas HTTP
  const handleApiResponse = async (response) => {
    if (response.status === 403) {
      try {
        const errorData = await response.json();
        if (errorData.error === "Token inválido" || errorData.error === "Token expirado") {
          handleTokenExpired();
          return null;
        }
      } catch (e) {
        // Si no se puede parsear la respuesta, asumir token inválido
        handleTokenExpired();
        return null;
      }
    }
    return response;
  };

  // Función helper para hacer peticiones con manejo automático de token expirado
  const fetchWithAuth = async (url, options = {}) => {
    const authToken = getToken();
    
    if (!authToken) {
      throw new Error('No hay token de autenticación');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
      ...options.headers
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      // Manejar respuesta automáticamente
      const handledResponse = await handleApiResponse(response);
      
      if (!handledResponse) {
        throw new Error('Sesión expirada');
      }

      if (!handledResponse.ok) {
        const errorData = await handledResponse.json();
        throw new Error(`HTTP error! status: ${handledResponse.status}, message: ${JSON.stringify(errorData)}`);
      }

      return handledResponse;
    } catch (error) {
      console.error('Error en fetchWithAuth:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      isAuthenticated, 
      login, 
      logout, 
      checkAuth,
      refreshToken,
      getToken,
      handleTokenExpired,
      fetchWithAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};